import { useRef, useState } from "react";
import styled from "styled-components";
import { colors } from "styles/color-tokens";
import EmojiPickerReact, { EmojiStyle, Theme } from "emoji-picker-react";

import { useClickOutside } from "libs/use-click-outside";
import { useOpenableState } from "libs/use-openable-state";
// @ts-ignore
import Smile from "../icons/smile.svg";
import { ActionButton } from "../v2/components/action-button";
import { TooltipWrapper } from "../../TooltipWrapper";

import { convertEmojiClickData, EmojiPickerResult } from "libs/emoji";

export type { EmojiPickerResult };

/** @deprecated Use EmojiPickerResult instead */
export type EmojiData = EmojiPickerResult;

const HEIGHT_OF_EMOJI_SELECTOR = 426.64;

interface EmojiPickerProps {
  className?: string;
  onSelect: (value: EmojiPickerResult) => void;
  leftAlign: boolean;
  newDesign?: boolean;
  bottomAligned?: boolean;
  compact?: boolean;
  nativeIcon?: boolean;
}

function NativeEmojiPicker({
  onSelect,
  bottomAligned,
  compact,
}: {
  onSelect: (emoji: EmojiPickerResult) => void;
  bottomAligned: boolean;
  compact?: boolean;
}) {
  return (
    <PickerContainer $bottomAligned={bottomAligned} $compact={compact}>
      <EmojiPickerReact
        className={compact ? "sked-emoji-picker-compact" : undefined}
        onEmojiClick={(emojiData) => onSelect(convertEmojiClickData(emojiData))}
        height={compact ? 320 : 400}
        width={compact ? 312 : 350}
        emojiStyle={EmojiStyle.NATIVE}
        theme={Theme.LIGHT}
        previewConfig={{ showPreview: false }}
      />
    </PickerContainer>
  );
}

const PickerContainer = styled.div<{
  $bottomAligned: boolean;
  $compact?: boolean;
}>`
  position: absolute;
  right: 0;
  ${({ $bottomAligned }) =>
    $bottomAligned
      ? `
    top: unset;
    bottom: 40px;
  `
      : `
    top: 100%;
  `}
  z-index: 1000;

  ${({ $compact }) =>
    $compact &&
    `
    .sked-emoji-picker-compact {
      --epr-horizontal-padding: 8px;
      --epr-header-padding: 8px var(--epr-horizontal-padding);
      --epr-search-input-height: 34px;
      --epr-search-input-padding: 0 24px;
      --epr-category-navigation-button-size: 24px;
      --epr-category-label-height: 28px;
      --epr-category-label-padding: 0 var(--epr-horizontal-padding);
      --epr-emoji-size: 22px;
      --epr-emoji-padding: 3px;
      --epr-preview-height: 0px;
      font-size: 12px;
    }

    .sked-emoji-picker-compact input {
      font-size: 14px;
    }

    .sked-emoji-picker-compact .epr-emoji-category-label {
      font-size: 14px;
      font-weight: 700;
    }
    .sked-emoji-picker-compact button {
      padding-left: 0;
      padding-right: 0;
    }
  `}
`;

export function EmojiPickerComponent(props: EmojiPickerProps) {
  const emojiModal = useOpenableState();
  const wrapperRef = useRef<HTMLDivElement>(null);
  useClickOutside(wrapperRef, emojiModal.close);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [bottomAligned, setBottomAligned] = useState(false);

  function onSelect(emoji: EmojiPickerResult) {
    props.onSelect(emoji);
  }

  const onMouseDown = (e: React.MouseEvent) => {
    // Prevent the contentEditable from losing focus/selection when the emoji
    // button is pressed. Without this, SkedCaption's handleFocus fires via
    // onMouseDown on the editor div and nulls out this.selection before
    // insertHtml() can restore it, causing the emoji to be lost.
    e.preventDefault();
    // check the distance between template button to bottom of the browser
    // if diff is less than height of the selector, bottom align it
    const bounding = buttonRef?.current?.getBoundingClientRect();
    const shouldBottomAlign =
      (bounding?.bottom ?? 0) + HEIGHT_OF_EMOJI_SELECTOR >
      window.innerHeight + 50;
    setBottomAligned(shouldBottomAlign);
    emojiModal.toggle();
  };

  return (
    <div ref={wrapperRef} className={props.className}>
      {props.newDesign ? (
        <TooltipWrapper
          tooltip={emojiModal.isOpened ? null : "Emojis"}
          placement={"top"}
        >
          {props.nativeIcon ? (
            <button
              type="button"
              ref={buttonRef}
              className="flex size-8 items-center justify-center rounded-lg border border-neutral-100 bg-white p-0 text-neutral-800 shadow-xs outline-none hover:border-neutral-100 hover:bg-neutral-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              aria-label="Add emoji"
              onMouseDown={onMouseDown}
            >
              <i
                aria-hidden="true"
                className="fa-regular fa-face-smile text-base"
              />
            </button>
          ) : (
            <ActionButton
              onMouseDown={onMouseDown}
              ref={buttonRef}
              focused={emojiModal.isOpened}
              className="shadow-xs"
            >
              <i className="fa-regular fa-face-smile  text-[12px]" />
            </ActionButton>
          )}
        </TooltipWrapper>
      ) : (
        <TemplateButton
          type="button"
          isOpen={emojiModal.isOpened}
          onMouseDown={onMouseDown}
          ref={buttonRef}
        >
          <Smile />
        </TemplateButton>
      )}

      {emojiModal.isOpened && (
        <NativeEmojiPicker
          onSelect={onSelect}
          bottomAligned={props.bottomAligned ?? bottomAligned}
          compact={props.compact}
        />
      )}
    </div>
  );
}

const TemplateButton = styled.button<{ isOpen: boolean }>`
  width: 24px;
  min-width: 24px;
  height: 24px;
  padding: 0;
  appearance: none;
  border: none;
  box-shadow: 0 1px 4px ${colors.black}33;
  border: 1px solid
    ${(props) => (props.isOpen ? colors.primary500 : colors.primary100)};
  border-radius: 5px;
  background: ${(props) =>
    props.isOpen ? colors.primary500 : colors.primary50};
`;

export const EmojiPicker = styled(EmojiPickerComponent)`
  position: relative;

  ${({ leftAlign }) =>
    leftAlign &&
    `
    ${PickerContainer} {
      left: 0;
      right: unset;
    }
  `}
`;
